// src/features/task/components/TaskListItem.tsx
// 可複用的 Task 清單行組件
//
// Checkbox（Square/CheckSquare）：切換 OPEN/CLOSED，任務不消失
// 桌面端：hover 行尾出現 QuickActions（Sparkles + Drop），取代 meta 資訊區
// 行動端：左滑出現 SwipeActions（Sparkles + Drop），水平位移 > 垂直才觸發
// Tap：呼叫 onTaskClick，開啟 TaskDetail modal

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { formatDistanceToNow } from "date-fns";
import {
  CircleSlash,
  Square,
  CheckSquare,
  Sparkles,
  Loader2,
} from "lucide-react";
import { Task, categoryColors } from "../../../types/task";

interface TaskListItemProps {
  task: Task;
  onTaskClick: (taskId: string) => void;
  onDrop: (taskId: string) => void; // 棄置任務（closeReason: dropped）
  onToggleStatus: (
    taskId: string,
    newStatus: "OPEN" | "CLOSED",
    category?: string,
  ) => void; // Checkbox 切換
  onOptimize?: (taskId: string) => void; // AI 優化內容（僅 OPEN task 顯示）
  isOptimizing?: boolean; // 是否正在等待 AI 回應（顯示 spinner）
}

const SWIPE_REVEAL_WIDTH = 112; // 2 個按鈕的寬度（每個 56px）
const SWIPE_THRESHOLD = 40;

const TaskListItem: React.FC<TaskListItemProps> = ({
  task,
  onTaskClick,
  onDrop,
  onToggleStatus,
  onOptimize,
  isOptimizing = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swipeDirectionLocked = useRef<"h" | "v" | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    swipeDirectionLocked.current = null;
  }, []);

  // 用原生 addEventListener + { passive: false }，才能呼叫 preventDefault()
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    const handleTouchMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - touchStartX.current;
      const dy = e.touches[0].clientY - touchStartY.current;

      if (swipeDirectionLocked.current === null) {
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 5) {
          swipeDirectionLocked.current = "h";
        } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 5) {
          swipeDirectionLocked.current = "v";
        }
      }

      if (swipeDirectionLocked.current !== "h") return;

      e.preventDefault(); // 阻止頁面捲動（需 passive: false 才有效）
      const offset = Math.max(-SWIPE_REVEAL_WIDTH, Math.min(0, dx));
      setSwipeOffset(offset);
      setIsSwiping(true);
    };

    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", handleTouchMove);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return;
    if (swipeOffset < -SWIPE_THRESHOLD) {
      setSwipeOffset(-SWIPE_REVEAL_WIDTH);
    } else {
      setSwipeOffset(0);
    }
    setIsSwiping(false);
    swipeDirectionLocked.current = null;
  }, [swipeOffset, isSwiping]);

  const collapseSwipe = useCallback(() => setSwipeOffset(0), []);

  const handleRowClick = useCallback(() => {
    if (Math.abs(swipeOffset) > 5) {
      collapseSwipe();
      return;
    }
    onTaskClick(task.id);
  }, [swipeOffset, collapseSwipe, onTaskClick, task.id]);

  const isClosed = task.status === "CLOSED";

  const createdAgo = useMemo(
    () =>
      task.createdAt
        ? formatDistanceToNow(task.createdAt, { addSuffix: false })
        : "",
    [task.createdAt],
  );

  return (
    <div className="relative overflow-hidden">
      {/* 行動端：左滑後顯示 Optimize + Drop 按鈕（Closed task 不顯示） */}
      {!isClosed && (
        <div
          className="absolute right-0 top-0 bottom-0 flex items-stretch md:hidden"
          style={{ width: SWIPE_REVEAL_WIDTH }}
          aria-hidden="true"
        >
          {/* Optimize（左側） */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              collapseSwipe();
              onOptimize?.(task.id);
            }}
            disabled={isOptimizing}
            className="w-14 flex flex-col items-center justify-center bg-sky-500 text-white text-xs gap-1 active:bg-sky-600 disabled:opacity-60"
            aria-label={`Optimize ${task.title}`}
            title="Optimize"
          >
            <Sparkles size={18} />
            <span>Optimize</span>
          </button>
          {/* Drop（右側） */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              collapseSwipe();
              onDrop(task.id);
            }}
            className="w-14 flex flex-col items-center justify-center bg-gray-500 text-white text-xs gap-1 active:bg-gray-600"
            aria-label={`Drop ${task.title}`}
            title="Drop"
          >
            <CircleSlash size={18} />
            <span>Drop</span>
          </button>
        </div>
      )}

      {/* 主行 */}
      <div
        ref={rowRef}
        className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors cursor-pointer select-none"
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: isSwiping ? "none" : "transform 0.2s ease",
        }}
        onClick={handleRowClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleStatus(
              task.id,
              isClosed ? "OPEN" : "CLOSED",
              task.category,
            );
          }}
          className="flex-shrink-0 text-gray-400 hover:text-teal-500 transition-colors"
          aria-label={
            isClosed ? `Reopen ${task.title}` : `Complete ${task.title}`
          }
        >
          {isClosed ? (
            <CheckSquare size={18} className="text-green-500" />
          ) : (
            <Square size={18} />
          )}
        </button>

        {/* 標題 */}
        <span
          className={`flex-1 text-sm truncate ${
            isClosed
              ? "text-gray-400 line-through"
              : isOptimizing
                ? "text-gray-400"
                : "text-gray-900"
          }`}
        >
          {task.title}
        </span>
        {isOptimizing && (
          <Loader2
            size={13}
            className="flex-shrink-0 text-sky-400 animate-spin"
          />
        )}

        {/* 桌面端：hover 時顯示 Sparkles + Drop，否則顯示 meta 資訊；固定高度避免行高跳動 */}
        <div className="hidden sm:flex flex-shrink-0 items-center gap-1 h-[28px]">
          {isHovered && !isClosed ? (
            <>
              {/* Optimize */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOptimize?.(task.id);
                }}
                disabled={isOptimizing}
                className="p-1.5 rounded-md text-sky-400 hover:text-sky-600 hover:bg-sky-50 transition-colors disabled:opacity-50"
                aria-label={`Optimize ${task.title}`}
                title="Optimize"
              >
                <Sparkles size={15} />
              </button>
              {/* Drop */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDrop(task.id);
                }}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label={`Drop ${task.title}`}
                title="Drop"
              >
                <CircleSlash size={15} />
              </button>
            </>
          ) : (
            <>
              {task.category && (
                <span
                  className={`inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[task.category] ?? "bg-gray-100 text-gray-600"}`}
                >
                  {task.category}
                </span>
              )}
              <span className="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">
                {createdAgo}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskListItem;
